// TradeHistory/index.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Stack,
  Typography,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  TextField,
  CircularProgress,
  Alert,
  alpha,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  AutoAwesome as AnalyzedIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  Info as InfoIcon,
  Assessment as AssessmentIcon,
  CallSplit as VariantIcon,
  Code as ConfigIcon,
} from '@mui/icons-material';
import axios from 'axios';

import { COLORS } from './constants';
import TradeTable from './TradeTable';
import PnLChart from './PnLChart';
import TradeSummary from './TradeSummary';

// ==================== MAIN COMPONENT ====================
const TradeHistory = ({ apiBaseUrl = window.location.origin }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const strategyId = searchParams.get('strategy');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTrades, setExpandedTrades] = useState({});
  const [tradeFilter, setTradeFilter] = useState('all');
  const [variantFilter, setVariantFilter] = useState('all');
  const [startDate, setStartDate] = useState('2025-10-01');
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  // Fetch trades
  const fetchData = useCallback(async () => {
    if (!strategyId) {
      setError('No strategy specified');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ broadcast: 'false' });
      if (startDate) params.set('start_date', startDate);
      if (endDate)   params.set('end_date',   endDate);
      const response = await axios.get(
        `${apiBaseUrl}/strategies/analyze/${strategyId}/?${params}`
      );
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load trades');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, strategyId, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Unique variant IDs present in this dataset
  const allVariants = useMemo(() => {
    if (!data?.trades) return [];
    const ids = [...new Set(data.trades.map(t => t.variant_id).filter(Boolean))];
    return ids.map(id => {
      const match = data.trades.find(t => t.variant_id === id);
      return { id, label: match?.variant_label || id };
    });
  }, [data?.trades]);

  // Filter trades
  const filteredTrades = useMemo(() => {
    if (!data?.trades) return [];
    let trades = data.trades;
    if (tradeFilter !== 'all') trades = trades.filter((t) => t.status === tradeFilter);
    if (variantFilter !== 'all') trades = trades.filter((t) => t.variant_id === variantFilter);
    return trades;
  }, [data?.trades, tradeFilter, variantFilter]);

  // Calculate stats for TradeSummary
  const stats = useMemo(() => {
    const summary = data?.summary || {};
    const slippageCostPct = 0.05;
    const validTrades = filteredTrades.filter(t => t.pnl_pct !== null && t.pnl_pct !== undefined);
    
    const getAdjustedPct = (t) => (parseFloat(t.pnl_pct) || 0) - slippageCostPct;
    const adjustedWins = validTrades.filter(t => getAdjustedPct(t) > 0);
    const adjustedLosses = validTrades.filter(t => getAdjustedPct(t) <= 0);
    
    const totalAdjustedPnLPct = validTrades.reduce((sum, t) => sum + getAdjustedPct(t), 0);
    const totalPnLPct = validTrades.reduce((sum, t) => sum + (parseFloat(t.pnl_pct) || 0), 0);
    
    return {
      total: validTrades.length,
      adjustedWinning: adjustedWins.length,
      adjustedLosing: adjustedLosses.length,
      adjustedWinRate: validTrades.length > 0 ? ((adjustedWins.length / validTrades.length) * 100).toFixed(1) : '0.0',
      totalAdjustedPnLPct,
      totalPnLPct,
      slippageCostPct,
    };
  }, [filteredTrades, data?.summary]);

  // Toggle trade expansion
  const handleToggleTrade = (tradeId) => {
    setExpandedTrades((prev) => ({
      ...prev,
      [tradeId]: !prev[tradeId],
    }));
  };

  // Export XLSX — built client-side from already-loaded trade data
  const handleExportCSV = () => {
    if (!filteredTrades.length) return;

    // Dynamic import keeps the bundle lean
    import('xlsx').then((XLSX) => {
      const calcDuration = (entry, exit) => {
        if (!entry || !exit) return '';
        const ms = new Date(exit) - new Date(entry);
        if (isNaN(ms) || ms < 0) return '';
        const totalMin = Math.floor(ms / 60000);
        const h = Math.floor(totalMin / 60);
        const m = totalMin % 60;
        return h > 0 ? `${h}h ${m}m` : `${m}m`;
      };

      // ── Trades sheet ──────────────────────────────────────────────────────
      const headers = [
        'Trade #', 'Variant',
        'Entry Sig', 'Entry Time', 'Entry Price',
        'Exit Sig',  'Exit Time',  'Exit Price',
        'Duration',  'P&L ($)',    'P&L %',      'Adj P&L %', 'Status',
        'High',      'High Time',  'High %',
        'Low',       'Low Time',   'Low %',
      ];

      const rows = filteredTrades.map((t) => [
        t.trade_number,
        t.variant_label || t.variant_id || '',
        t.entry_signal  || '',
        t.entry_time    || '',
        t.entry_price   ?? '',
        t.exit_signal   || '',
        t.exit_time     || '',
        t.exit_price    ?? '',
        calcDuration(t.entry_time, t.exit_time),
        t.pnl           ?? '',
        t.pnl_pct       ?? '',
        t.adjusted_pnl_pct ?? '',
        t.status        || '',
        t.trade_high    ?? '',
        t.high_time     || '',
        t.high_pct != null ? `+${t.high_pct}%` : '',
        t.trade_low     ?? '',
        t.low_time      || '',
        t.low_pct  != null ? `${t.low_pct}%`  : '',
      ]);

      const tradeSheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

      // Column widths
      const colWidths = [6,28,14,22,13,14,22,13,10,12,10,10,12,13,22,10,13,22,10];
      tradeSheet['!cols'] = colWidths.map((w) => ({ wch: w }));

      // ── Summary sheet ─────────────────────────────────────────────────────
      const s = data?.summary || {};
      const summaryData = [
        ['Strategy', strategyId],
        ['Total Trades',    s.total_trades       ?? ''],
        ['Completed',       s.completed_trades   ?? ''],
        ['Open',            s.open_trades        ?? ''],
        ['Winning',         s.winning_trades     ?? ''],
        ['Losing',          s.losing_trades      ?? ''],
        ['Win Rate (%)',    s.win_rate != null ? +s.win_rate.toFixed(1) : ''],
        ['Total P&L ($)',   s.total_pnl          ?? ''],
        ['Avg P&L ($)',     s.average_pnl        ?? ''],
        ['Adj Win Rate (%)',s.adjusted_win_rate != null ? +s.adjusted_win_rate.toFixed(1) : ''],
        ['Total Adj P&L %', s.total_adjusted_pnl_pct ?? ''],
      ];
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      summarySheet['!cols'] = [{ wch: 20 }, { wch: 30 }];

      // ── Workbook ──────────────────────────────────────────────────────────
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, tradeSheet,   'Trades');
      XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

      const date    = new Date().toISOString().split('T')[0];
      const safeName = (strategyId || 'trades').replace(/[/\\?%*:|"<>]/g, '_');
      XLSX.writeFile(wb, `${safeName}_trades_${date}.xlsx`);
    });
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: COLORS.bg.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress sx={{ color: COLORS.primary }} />
      </Box>
    );
  }

  const summary = data?.summary || {};
  const config = data?.strategy_config || {};
  const totalPnl = summary.total_pnl || 0;
  const winRate = summary.win_rate || 0;
  const netReturn = stats.totalAdjustedPnLPct;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: COLORS.bg.primary, p: 3 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          bgcolor: alpha(COLORS.bg.secondary, 0.8),
          border: `1px solid ${alpha(COLORS.border, 0.5)}`,
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: COLORS.text.muted }}>
              <BackIcon />
            </IconButton>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography variant="h5" sx={{ color: COLORS.text.primary, fontWeight: 700 }}>
                  {strategyId}
                </Typography>
                <Chip
                  icon={<AnalyzedIcon sx={{ fontSize: 14 }} />}
                  label={`${summary.total_trades || 0} Analyzed`}
                  size="small"
                  sx={{
                    bgcolor: alpha(COLORS.success, 0.15),
                    color: COLORS.success,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    '& .MuiChip-icon': { color: COLORS.success },
                  }}
                />
              </Stack>
              <Typography variant="body2" sx={{ color: COLORS.text.muted, mt: 0.5 }}>
                Win Rate: {winRate.toFixed(1)}% • Net Return: {netReturn >= 0 ? '+' : ''}{netReturn.toFixed(2)}%
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            {/* Date range */}
            <TextField
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              inputProps={{ max: endDate }}
              sx={{
                width: 150,
                '& .MuiOutlinedInput-root': {
                  bgcolor: alpha(COLORS.bg.tertiary, 0.5),
                  color: COLORS.text.primary,
                  '& fieldset': { borderColor: alpha(COLORS.border, 0.5) },
                  '&:hover fieldset': { borderColor: COLORS.primary },
                  '&.Mui-focused fieldset': { borderColor: COLORS.primary },
                },
                '& input': { colorScheme: 'dark', fontSize: '0.82rem' },
              }}
            />
            <Typography sx={{ color: COLORS.text.muted, fontSize: '0.8rem' }}>–</Typography>
            <TextField
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              inputProps={{ min: startDate }}
              sx={{
                width: 150,
                '& .MuiOutlinedInput-root': {
                  bgcolor: alpha(COLORS.bg.tertiary, 0.5),
                  color: COLORS.text.primary,
                  '& fieldset': { borderColor: alpha(COLORS.border, 0.5) },
                  '&:hover fieldset': { borderColor: COLORS.primary },
                  '&.Mui-focused fieldset': { borderColor: COLORS.primary },
                },
                '& input': { colorScheme: 'dark', fontSize: '0.82rem' },
              }}
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={tradeFilter}
                onChange={(e) => setTradeFilter(e.target.value)}
                sx={{
                  bgcolor: alpha(COLORS.bg.tertiary, 0.5),
                  color: COLORS.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(COLORS.border, 0.5),
                  },
                  '& .MuiSelect-icon': { color: COLORS.text.muted },
                }}
              >
                <MenuItem value="all">All Trades</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="open">Open</MenuItem>
              </Select>
            </FormControl>
            {allVariants.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={variantFilter}
                  onChange={(e) => setVariantFilter(e.target.value)}
                  sx={{
                    bgcolor: alpha(COLORS.bg.tertiary, 0.5),
                    color: COLORS.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(COLORS.border, 0.5),
                    },
                    '& .MuiSelect-icon': { color: COLORS.text.muted },
                  }}
                >
                  <MenuItem value="all">All Variants</MenuItem>
                  {allVariants.map(v => (
                    <MenuItem key={v.id} value={v.id}>{v.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <IconButton
              onClick={handleExportCSV}
              sx={{
                bgcolor: alpha(COLORS.bg.tertiary, 0.5),
                color: COLORS.text.muted,
                '&:hover': { bgcolor: alpha(COLORS.primary, 0.1), color: COLORS.primary },
              }}
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              onClick={fetchData}
              sx={{
                bgcolor: alpha(COLORS.bg.tertiary, 0.5),
                color: COLORS.primary,
                '&:hover': { bgcolor: alpha(COLORS.primary, 0.1) },
              }}
            >
              <RefreshIcon />
            </IconButton>
            <IconButton
              onClick={() => navigate(`/officer/strategy-config?open=${encodeURIComponent(strategyId)}`)}
              title="Open strategy config"
              sx={{
                bgcolor: alpha(COLORS.bg.tertiary, 0.5),
                color: COLORS.warning,
                '&:hover': { bgcolor: alpha(COLORS.warning, 0.1) },
              }}
            >
              <ConfigIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Paper>

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 3, bgcolor: alpha(COLORS.danger, 0.1) }}
        >
          {error}
        </Alert>
      )}

      {/* Strategy Config Chips */}
      <Stack direction="row" spacing={1} sx={{ mb: 3 }} flexWrap="wrap">
        <Chip
          icon={<InfoIcon sx={{ fontSize: 14 }} />}
          label="Strategy Config"
          size="small"
          sx={{
            bgcolor: alpha(COLORS.primary, 0.1),
            color: COLORS.primary,
            fontWeight: 600,
            '& .MuiChip-icon': { color: COLORS.primary },
          }}
        />
        <Chip
          label={config.symbol || 'NQ'}
          size="small"
          sx={{ bgcolor: alpha(COLORS.primary, 0.15), color: COLORS.primary, fontWeight: 600 }}
        />
        <Chip
          label={config.timeframe || '1m'}
          size="small"
          sx={{ bgcolor: alpha(COLORS.warning, 0.15), color: COLORS.warning, fontWeight: 600 }}
        />
        <Chip
          label={config.direction || 'LONG'}
          size="small"
          sx={{
            bgcolor: alpha(config.direction === 'SHORT' ? COLORS.danger : COLORS.success, 0.15),
            color: config.direction === 'SHORT' ? COLORS.danger : COLORS.success,
            fontWeight: 600,
          }}
        />
        <Chip
          label={config.trading_hour || 'ETH'}
          size="small"
          sx={{ bgcolor: alpha(COLORS.purple, 0.15), color: COLORS.purple, fontWeight: 600 }}
        />
      </Stack>

      {/* Variant Breakdown — only for composite strategies */}
      {allVariants.length > 1 && data?.summary?.variant_breakdown && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 3 }} flexWrap="wrap">
          {Object.entries(data.summary.variant_breakdown).map(([variantId, vb], idx) => {
            const VARIANT_PALETTE = ['#00d4ff', '#8b5cf6', '#f59e0b', '#ec4899', '#10b981', '#f97316'];
            const color = VARIANT_PALETTE[idx % VARIANT_PALETTE.length];
            const winRate = vb.win_rate ?? (vb.completed_trades > 0 ? (vb.winning_trades / vb.completed_trades) * 100 : 0);
            return (
              <Paper
                key={variantId}
                elevation={0}
                sx={{
                  p: 1.5,
                  bgcolor: alpha(color, 0.06),
                  border: `1px solid ${alpha(color, 0.25)}`,
                  borderRadius: 2,
                  flex: '1 1 200px',
                  minWidth: 180,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                  <VariantIcon sx={{ color, fontSize: 14 }} />
                  <Typography sx={{ color, fontWeight: 700, fontSize: '0.75rem', flexGrow: 1 }}>
                    {vb.label || variantId}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label={`${vb.total_trades ?? 0} trades`} size="small" sx={{ bgcolor: alpha(color, 0.12), color, fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                  <Chip label={`${typeof winRate === 'number' ? winRate.toFixed(1) : winRate}% WR`} size="small" sx={{ bgcolor: alpha(color, 0.12), color, fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                  <Chip
                    label={`${(vb.total_pnl ?? 0) >= 0 ? '+' : ''}$${(vb.total_pnl ?? 0).toFixed(2)}`}
                    size="small"
                    sx={{
                      bgcolor: alpha((vb.total_pnl ?? 0) >= 0 ? COLORS.success : COLORS.danger, 0.12),
                      color: (vb.total_pnl ?? 0) >= 0 ? COLORS.success : COLORS.danger,
                      fontWeight: 700, fontSize: '0.65rem', height: 20,
                    }}
                  />
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* P&L Chart (50%) + Trade Summary (30%) + Empty (20%) */}
      {filteredTrades.length > 0 && (
        <Stack 
          direction={{ xs: 'column', lg: 'row' }} 
          spacing={2.5} 
          sx={{ mb: 3 }}
        >
          {/* P&L Chart - 50% */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              bgcolor: alpha(COLORS.bg.secondary, 0.8),
              border: `1px solid ${alpha(COLORS.border, 0.5)}`,
              borderRadius: 2,
              height: 400,
              flex: { lg: '0 0 calc(50% - 10px)' },
              minWidth: 0,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <TrendingUpIcon sx={{ color: COLORS.success, fontSize: 18 }} />
              <Typography sx={{ color: COLORS.text.primary, fontWeight: 700, fontSize: '0.9rem' }}>
                P&L Performance
              </Typography>
              <Chip
                label={`${totalPnl >= 0 ? '+' : ''}$${totalPnl.toFixed(2)}`}
                size="small"
                sx={{
                  bgcolor: alpha(totalPnl >= 0 ? COLORS.success : COLORS.danger, 0.15),
                  color: totalPnl >= 0 ? COLORS.success : COLORS.danger,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  height: 22,
                }}
              />
            </Stack>
            <PnLChart trades={filteredTrades} height={320} />
          </Paper>

          {/* Trade Summary - 30% */}
          <Paper
            elevation={0}
            sx={{
              p: 1.25,
              bgcolor: alpha(COLORS.bg.secondary, 0.8),
              border: `1px solid ${alpha(COLORS.border, 0.5)}`,
              borderRadius: 2,
              height: 400,
              flex: { lg: '0 0 calc(30% - 10px)' },
              minWidth: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5, pb: 0.5, borderBottom: `1px solid ${alpha(COLORS.border, 0.5)}`, flexShrink: 0 }}>
              <AssessmentIcon sx={{ color: COLORS.purple, fontSize: 14 }} />
              <Typography sx={{ color: COLORS.text.primary, fontWeight: 700, fontSize: '0.75rem' }}>Performance</Typography>
            </Stack>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <TradeSummary stats={stats} trades={filteredTrades} />
            </Box>
          </Paper>

          {/* Empty space - 20% (flex handles it) */}
        </Stack>
      )}

      {/* Trade Log */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: alpha(COLORS.bg.secondary, 0.8),
          border: `1px solid ${alpha(COLORS.border, 0.5)}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Trade Log Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${alpha(COLORS.border, 0.5)}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <HistoryIcon sx={{ color: COLORS.primary }} />
            <Typography variant="h6" sx={{ color: COLORS.text.primary, fontWeight: 600 }}>
              Trade Log
            </Typography>
            <Chip
              label={`${filteredTrades.length} trades`}
              size="small"
              sx={{
                bgcolor: alpha(COLORS.primary, 0.15),
                color: COLORS.primary,
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            />
          </Stack>
        </Box>

        {/* Trade Table */}
        <TradeTable
          trades={filteredTrades}
          expandedTrades={expandedTrades}
          onToggleTrade={handleToggleTrade}
        />
      </Paper>
    </Box>
  );
};

export default TradeHistory;
